import DescriptionIcon from "@mui/icons-material/Description";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import GridOnIcon from "@mui/icons-material/GridOn";
import { IconButton, ListItemIcon, ListItemText, MenuItem, MenuList, Tooltip } from "@mui/material";
import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemReactTinyPopover } from "../../Popups/SystemReactTinyPopover";
import type { ExportFormat } from "./exportGridData";

const FORMAT_MENU: Record<ExportFormat, { labelKey: string; Icon: typeof GridOnIcon }> = {
	xlsx: { labelKey: "system.dataGrid.exportXlsx", Icon: GridOnIcon },
	csv: { labelKey: "system.dataGrid.exportCsv", Icon: DescriptionIcon },
};

interface GridExportButtonProps {
	onExport: (format: ExportFormat) => void;
	/** Formats to offer, in menu order. With exactly one, the button downloads it directly (no menu). */
	formats: readonly ExportFormat[];
	disabled?: boolean;
	dataTestId?: string;
}

/** Toolbar download button: opens a small menu to pick the export format, or downloads the only one offered. */
export const GridExportButton = memo(function GridExportButton({ onExport, formats, disabled, dataTestId }: GridExportButtonProps) {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const onlyFormat = formats.length === 1 ? formats[0] : undefined;

	const handlePick = useCallback(
		(format: ExportFormat) => {
			setIsOpen(false);
			onExport(format);
		},
		[onExport],
	);

	return (
		<SystemReactTinyPopover
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			positions={["bottom", "top", "left", "right"]}
			align="end"
			content={
				<MenuList disablePadding>
					{formats.map((format) => {
						const { labelKey, Icon } = FORMAT_MENU[format];
						return (
							<MenuItem key={format} onClick={() => handlePick(format)} data-testid={dataTestId ? `${dataTestId}-${format}` : undefined}>
								<ListItemIcon>
									<Icon fontSize="small" />
								</ListItemIcon>
								<ListItemText primary={t(labelKey)} />
							</MenuItem>
						);
					})}
				</MenuList>
			}
		>
			<Tooltip title={t("system.dataGrid.export")}>
				{/* span so the tooltip still works while the button is disabled */}
				<span>
					<IconButton
						size="small"
						color="primary"
						aria-label={t("system.dataGrid.export")}
						onClick={() => (onlyFormat ? onExport(onlyFormat) : setIsOpen((prev) => !prev))}
						disabled={disabled}
						data-testid={dataTestId}
					>
						<FileDownloadIcon fontSize="small" />
					</IconButton>
				</span>
			</Tooltip>
		</SystemReactTinyPopover>
	);
});
